/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/store";
import useApi from "../hooks/useApi";
import {
  GetAllFilesAndFoldersByUserDto,
  GetAllFilesFoldersRequestDto,
} from "../types/FolderPermission";
import { getAllFilesFolderByUser } from "../services/roleService";
import { clearResourceModified } from "../redux/resourceModifiedSlice";
import { clearResourceDeleted } from "../redux/resourceDeletedSlice";
import { GLOBALSETTINGS, SYSTEMSETTINGS } from "../data/constants";
import { MoveFolderDto } from "../types/Folder";
import { moveFolder } from "../services/folderService";
import { useSettingValue } from "../services/useSettingValue";
import { DataGridProProps, useGridApiRef } from "@mui/x-data-grid-pro";
import React from "react";
import { filterDataBySearch, handleScrollPositionChange } from "../utils/commonHelper";
import { clearResourceAdded } from "../redux/resourceAddedSlice";

const useFolderList = (userId: number | null, apiRef: ReturnType<typeof useGridApiRef>) => {
  const dispatch = useDispatch();
  const { loading, executeApi } = useApi();

  const { parentFolderId, trigger } = useSelector((state: RootState) => state.resourceCreated);
  const addedRowState = useSelector((state: RootState) => state.resourceAddedSlice);
  const modifiedSubFolderFile = useSelector((state: RootState) => state.resourceModified);
  const deletedFile = useSelector((state: RootState) => state.resourceDeleted);
  const settingValue = useSettingValue(SYSTEMSETTINGS.RESOURCE_PAGINATION);

  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{
    data: GetAllFilesAndFoldersByUserDto[] | null;
    total: number;
  }>({
    data: null,
    total: 0,
  });
  const [isClientSearchEvaluated, setIsClientSearchEvaluated] = useState(false);
  const [searchResult, setSearchResult] = useState("");
  const [allData, setAllData] = useState<GetAllFilesAndFoldersByUserDto[]>([]);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = useRef(true);
  const scrollContainerRef = useRef(null);
  const isGrid2Fetching = useRef(false);
  
  React.useEffect(() => {
    apiRef?.current?.subscribeEvent("scrollPositionChange", handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });

  const prevPagination = useRef(paginationModel);
  const prevSearchResult = useRef(searchResult);

  const handleOnRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return; // Prevent further API calls when all rows are loaded
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const handleGridScroll = () => {
    if (!scrollContainerRef.current || loading || isGrid2Fetching.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    // Check if user has scrolled to the bottom
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      isGrid2Fetching.current = true;

      setPaginationModel((prev) => {
        const totalPages = Math.ceil(pageState?.total / prev.pageSize);
        if (prev.page + 1 < totalPages) {
          return { ...prev, page: prev.page + 1 };
        }
        return prev;
      });

      // Reset the fetching flag after a short delay
      setTimeout(() => (isGrid2Fetching.current = false), 200);
    }
  };

  const moveFolderOrFile = async (moveFolderDto: MoveFolderDto) => {
    return await executeApi<MoveFolderDto, boolean>(moveFolder, moveFolderDto);
  };

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, []);


  const fetchAllFilesFolders = useCallback(
    async (selectedFolderId: number | null, searchOverride?: string) => {
      if (userId === null) return;
      const activeSearch = searchOverride !== undefined ? searchOverride : searchResult;
      const isNewSearch = paginationModel.page === 0;

      // If already in client search mode & first load happened → filter local data
      if (isClientSearchEvaluated && isFirstLoaded.current) {
        if (activeSearch) {
          const lowerText = activeSearch.toLowerCase();
          const filtered = filterDataBySearch(allData, lowerText);
          setPageState({ data: filtered ?? [], total: filtered?.length ?? 0 });
        } else {
          // Reset to full dataset
          setPageState({ data: allData ?? [], total: allData?.length ?? 0 });
        }
        return;
      }

      // Otherwise → fetch from server
      const requestData: GetAllFilesFoldersRequestDto = {
        userId,
        parentFolderId: selectedFolderId,
        isDeleted: false,
        searchTerm: activeSearch,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      };

      const allFilesFolders = await executeApi<
        GetAllFilesFoldersRequestDto,
        GetAllFilesAndFoldersByUserDto[]
      >(getAllFilesFolderByUser, requestData);

      if (allFilesFolders && allFilesFolders.length > 0) {
        const totalRecords = allFilesFolders[0]?.totalRecordCount ?? 0;

        // Decide if we should enable client search mode
        if (!isFirstLoaded.current && totalRecords <= paginationModel.pageSize) {
          setIsClientSearchEvaluated(true);
          setAllData(allFilesFolders); // Store for client search
        } else {
          setIsClientSearchEvaluated(false);
          setAllData([]); // Avoid stale data for server search mode
        }

        isFirstLoaded.current = true;

        // Avoid duplicates when appending
        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.roleFolderPermissionsId));
          const newData = allFilesFolders.filter(
            (row) => !existingIds.has(row.roleFolderPermissionsId)
          );

          return {
            data: isNewSearch ? allFilesFolders : [...(prev.data || []), ...newData],
            total: isNewSearch ? totalRecords : prev.total,
          };
        });
      } else {
        setPageState({ data: [], total: 0 });
        setAllData([]);
        setIsClientSearchEvaluated(false);
      }

      // Restore scroll position
      setTimeout(() => {
        apiRef?.current?.scrollToIndexes({ rowIndex: apiRef.current.getScrollPosition().top });
      }, 100);
    },
    [userId, paginationModel, searchResult, allData, isClientSearchEvaluated]
  );

  useEffect(() => {
    if (settingValue) {
      const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

      setPaginationModel((prev) => {
        if (prev.pageSize !== newPageSize) {
          return { page: 0, pageSize: newPageSize };
        }
        return prev;
      });
      setPaginationInitialized(true);
    }
  }, [settingValue]);

  useEffect(() => {
    if (addedRowState.addedRow) {
      const newRow = {
        ...addedRowState.addedRow,
        roleFolderPermissionsId:
          addedRowState.addedRow.roleFolderPermissionsId > 0 ?
            addedRowState.addedRow.roleFolderPermissionsId : -Date.now(),
      };

      setAllData((prevState) => {
        const exists = prevState?.some(
          (folderFile) =>
            folderFile.roleFolderPermissionsId === newRow.roleFolderPermissionsId
        );

        return exists
          ? prevState.map((folderFile) =>
            folderFile.roleFolderPermissionsId === newRow.roleFolderPermissionsId
              ? { ...folderFile, ...newRow }
              : folderFile
          )
          : [newRow, ...(prevState ?? [])];
      });


      const row = addedRowState.addedRow;
      const searchResultRows = filterDataBySearch([row], searchResult);

      if (searchResultRows.length > 0) {
        setPageState((prevState) => {
          const exists = prevState.data?.some(
            (folderFile) =>
              folderFile.roleFolderPermissionsId === newRow.roleFolderPermissionsId
          );

          return {
            ...prevState,
            data: exists
              ? prevState.data.map((folderFile) =>
                folderFile.roleFolderPermissionsId === newRow.roleFolderPermissionsId
                  ? { ...folderFile, ...newRow }
                  : folderFile
              )
              : [newRow, ...(prevState.data ?? [])],
          };
        });
      }

      setIsClientSearchEvaluated(true);
      isFirstLoaded.current = true;

      dispatch(clearResourceAdded());
    }
  }, [addedRowState.addedRow, dispatch]);

  const getRowKey = (row: { folderId?: number; fileId?: number }) =>
    row.folderId && row.folderId > 0 ? `f-${row.folderId}` : `fl-${row.fileId}`;
  // Handle updates when a folder or file is modified
  useEffect(() => {
    if (modifiedSubFolderFile.modifiedRow) {
      const modifiedKey = getRowKey(modifiedSubFolderFile.modifiedRow);

      setAllData((prevState) =>
        prevState?.map((folderFile) =>
          getRowKey(folderFile) === modifiedKey
            ? {
                ...folderFile,
                ...modifiedSubFolderFile.modifiedRow,
                roleFolderPermissionsId: folderFile.roleFolderPermissionsId,
              }
            : folderFile
        ) ?? []
      );

      setPageState((prevState) => {
        const newData =
          prevState.data?.map((folderFile) =>
            getRowKey(folderFile) === modifiedKey
              ? {
                  ...folderFile,
                  ...modifiedSubFolderFile.modifiedRow,
                  roleFolderPermissionsId: folderFile.roleFolderPermissionsId,
                }
              : folderFile
          ) ?? [];

        const filtered = searchResult
          ? filterDataBySearch(newData, searchResult.toLowerCase())
          : newData;

        return { ...prevState, data: filtered };
      });


      dispatch(clearResourceModified());
    }
  }, [modifiedSubFolderFile, dispatch, searchResult]);


  // Handle deleted files or folders
  useEffect(() => {
  // Delete folder
    if (deletedFile.folderId > -1) {
      setAllData(prev =>
        prev?.filter(row => row.folderId !== deletedFile.folderId) ?? []
      );
      setPageState(prev => ({
        ...prev,
        data:
          prev.data?.filter(
            row => row.folderId !== deletedFile.folderId
          ) ?? [],
      }));
    }

    // Delete file
    if (deletedFile.fileId > -1) {
      setAllData(prev =>
        prev?.filter(row => row.fileId !== deletedFile.fileId) ?? []
      );
      setPageState(prev => ({
        ...prev,
        data:
          prev.data?.filter(
            row => row.fileId !== deletedFile.fileId
          ) ?? [],
      }));
    }

    if (deletedFile.folderId > -1 || deletedFile.fileId > -1) {
      dispatch(clearResourceDeleted());
    }
  }, [deletedFile, dispatch]);


  // Fetch data when folder changes
  useEffect(() => {
    if (isPaginationInitialized && trigger > -1 && parentFolderId !== -1) {
      setAllData([]);
      setIsClientSearchEvaluated(false);
      isFirstLoaded.current = false;
      setSearchResult("");
      prevSearchResult.current = "";
      if (prevPagination.current.page === 0) {
        fetchAllFilesFolders(parentFolderId, "");
      } else {
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));
      }
    }
  }, [parentFolderId, trigger, isPaginationInitialized]);

  useEffect(() => {
    const hasChanged = JSON.stringify(prevPagination.current) !== JSON.stringify(paginationModel);
    const searchChanged = JSON.stringify(prevSearchResult.current) !== JSON.stringify(searchResult);
    if (hasChanged || searchChanged) {
      prevPagination.current = paginationModel;
      prevSearchResult.current = searchResult;
      fetchAllFilesFolders(parentFolderId);
    }
  }, [fetchAllFilesFolders]);

  return {
    loading,
    pageState,
    paginationModel,
    setPaginationModel,
    fetchAllFilesFolders,
    moveFolderOrFile,
    handleOnRowsScrollEnd,
    handleSearchText,
    handleGridScroll,
    scrollContainerRef
  };
};

export default useFolderList;
